import { useMemo, useState } from 'react'
import { Modal } from './Modal'
import { Thumb } from './Thumb'
import type { Category, Item } from '@shared/types'

interface Props {
  category: Category
  items: Item[]
  /** Overrides "Add <category>", e.g. when replacing. */
  title?: string
  /** Item ids most recently packed first; those float to the top. */
  recentIds?: string[]
  /** Prefills the search box, so returning from a cancelled new food lands where it left. */
  initialQuery?: string
  onPick: (itemId: string) => void
  /**
   * Invent a food the pantry does not have, named by whatever was typed
   * (empty when nothing was). Omit to leave the picker a closed list.
   */
  onCreate?: (name: string) => void
  onClose: () => void
}

/**
 * Tap-to-add. This is the primary way to fill a slot on a phone, where
 * dragging across a scrolling board is fiddly.
 *
 * It also invents foods. A picker that can only offer what is already in the
 * pantry makes "okra" a dead end at the exact moment someone is holding the
 * okra, and answering it with "add some on the Pantry tab" asks them to leave
 * the day they are in the middle of building and find their way back. So the
 * offer sits under the list whether or not a search found nothing: not
 * finding it by browsing is the same problem as not finding it by typing.
 * What the offer opens is the full item editor, prefilled -- the moment a
 * food is invented is the moment its packet is in someone's hand, and an
 * untagged food passes every allergy and restriction check in silence.
 */
export function ItemPicker({ category, items, title, recentIds = [], initialQuery = '', onPick, onCreate, onClose }: Props) {
  const [query, setQuery] = useState(initialQuery)

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const list = needle
      ? items.filter((i) => i.name.toLowerCase().includes(needle) || i.notes.toLowerCase().includes(needle))
      : items
    // Recently packed first, in recency order; the rest keep pantry order.
    const rank = new Map(recentIds.map((id, i) => [id, i]))
    return [...list].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity))
  }, [items, query, recentIds])
  const recentCount = useMemo(() => shown.filter((i) => recentIds.includes(i.id)).length, [shown, recentIds])

  const kind = category.name.toLowerCase()
  const typed = query.trim()

  return (
    <Modal title={title ?? `Add ${category.name}`} onClose={onClose}>
      <input
        className="pantry-search"
        type="search"
        autoComplete="off"
        placeholder={`Search ${kind}…`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={`Search ${category.name}`}
        autoFocus
      />
      {shown.length === 0 ? (
        <p className="muted picker-none">
          {items.length === 0 ? `No ${kind} in the pantry yet.` : `Nothing matches “${query}”.`}
        </p>
      ) : (
        <div className="picker-list">
          {shown.map((item, i) => (
            <button key={item.id} className={`picker-option ${i < recentCount ? 'recent' : ''}`} onClick={() => onPick(item.id)}>
              <Thumb emoji={item.emoji} imageKey={item.imageKey} name={item.name} />
              <span className="name">
                {item.name}
                {item.notes && <div className="note muted">{item.notes}</div>}
              </span>
            </button>
          ))}
        </div>
      )}

      {onCreate && (
        <button type="button" className="picker-new" onClick={() => onCreate(typed)}>
          <span className="picker-new-mark" aria-hidden="true">
            +
          </span>
          <span className="name">
            {typed ? `Add “${typed}” as a ${kind}` : `Add a new ${kind}`}
            <div className="note muted">Goes in your pantry, and in this slot.</div>
          </span>
        </button>
      )}
    </Modal>
  )
}
